import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router";
import { useAuth, useLinks, useMissionRoleApplicant, useProfile } from "hooks";
import { APPLICANT_TYPES, APPLICATION_STEPS } from "constants/index";
import { Col } from "components/Containers";
import ApplicationSubmitButton from "./ApplicationSubmitButton";
import { ProgressChecklist } from "components/Progress";

/**
 * ApplicantProgress
 *
 * @param {Object} missionRoleApplicant
 * @param {Function} setRateEditing
 * @param {Function} setQuestionnaireEditing
 * @param {Boolean} hideSubmitButton
 * @param {Boolean} hideProgress
 */
const ApplicantProgress = ({
  missionRoleApplicant,
  setRateEditing,
  setQuestionnaireEditing,
  setRequirementsEditing,
  hideSubmitButton,
  hideProgress,
  ...props
}) => {
  const { isPartner, isCandidate, isFreelancer } = useAuth();
  const [canSubmit, setCanSubmit] = useState(false);
  const [completedSteps, setCompletedSteps] = useState([]);
  const { getApplicationSteps } = useMissionRoleApplicant({});
  const { isProfileComplete, data } = useProfile({
    profileId: missionRoleApplicant?.profileId || missionRoleApplicant?.profile?.id,
  });
  const links = useLinks();
  const navigate = useNavigate();
  const hasPreferredRate = data?.profile?.rate?.amount > 0;

  useEffect(() => {
    if (!missionRoleApplicant) return;

    const { applicationChecklist = [], appliedAt, state } = missionRoleApplicant;

    const isStepFulfilled = (step) =>
      step.name === APPLICATION_STEPS.profile_completion ? Boolean(isProfileComplete) : Boolean(step.fulfilled);

    const completedStepNames = applicationChecklist.filter(isStepFulfilled).map((step) => step.name.toUpperCase());

    const canSubmit = !appliedAt && state === APPLICANT_TYPES.pending;

    setCompletedSteps(completedStepNames);
    setCanSubmit(canSubmit);
  }, [missionRoleApplicant, isProfileComplete]);

  const handleStepClick = (step) => {
    switch (step) {
      case APPLICATION_STEPS.application_rate:
        return () => (!missionRoleApplicant?.appliedAt ? setRateEditing?.(true) : {});
      case APPLICATION_STEPS.questionnaire:
        return () => (!missionRoleApplicant?.appliedAt ? setQuestionnaireEditing?.(true) : {});
      case APPLICATION_STEPS.requirements:
        return () => (!missionRoleApplicant?.appliedAt ? setRequirementsEditing?.(true) : {});
      case APPLICATION_STEPS.video_interview:
        return () =>
          isPartner || isCandidate ? {} : navigate(links.profile_video_interviews(missionRoleApplicant?.profile?.slug));
      case APPLICATION_STEPS.profile_completion:
        return () =>
          isPartner || isCandidate ? {} : navigate(links.private_profile(missionRoleApplicant?.profile?.slug));
      default:
        return () => {};
    }
  };

  const getSteps = (checklist) => {
    return getApplicationSteps(checklist)?.map((step) => ({
      name: step.name,
      label: `Missions.Applicants.ApplicantProgress.Step.${step.name}`,
      isComplete:
        step.name === APPLICATION_STEPS.profile_completion && isFreelancer ? isProfileComplete : step.fulfilled,
      onClick: handleStepClick(step.name),
    }));
  };

  const steps = getSteps(missionRoleApplicant?.applicationChecklist);

  return (
    <Col gap="1rem">
      {!hideProgress && (
        <ProgressChecklist title="Missions.Applicants.ApplicantProgress.Progress.Title" steps={steps} {...props} />
      )}
      {!hideSubmitButton && (
        <ApplicationSubmitButton
          missionRoleApplicant={missionRoleApplicant}
          canSubmit={canSubmit}
          completedSteps={completedSteps}
          hasPreferredRate={hasPreferredRate}
        />
      )}
    </Col>
  );
};

ApplicantProgress.propTypes = {
  missionRoleApplicant: PropTypes.object.isRequired,
  setRateEditing: PropTypes.func.isRequired,
  setQuestionnaireEditing: PropTypes.func.isRequired,
  setRequirementsEditing: PropTypes.func.isRequired,
  hideSubmitButton: PropTypes.bool,
  hideProgress: PropTypes.bool,
};

export default ApplicantProgress;
